import { DisplayChatMessage } from 'interfaces/UserChat'
import MenuIcon from 'assets/icons/report_flag.svg'
import { useToggleState } from 'lib/hooks/useToggleState'
import FeatureDialog from 'stories/elements/Modal/DialogWindow/FeatureDialog'
import styled from 'styled-components'
import Stack from 'components/elements/Stack'
import { FormHandles, SubmitHandler } from '@unform/core'
import { useEffect, useRef, useState } from 'react'
import { Form } from '@unform/web'
import Textarea from 'components/elements/Inputs/Textarea'
import NewButton from 'components/elements/NewButton'
import { Body1, ButtonText } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import _useTranslation from 'lib/hooks/_useTranslation'
import { trackPromise } from 'react-promise-tracker'
import { ApiError } from 'services/ApiService'
import UserReportService from 'services/UserReportService'
import { ReportReason, ReportType, UserReportRequest } from 'interfaces/UserReport'
import { toast } from 'react-toastify'
import { Know } from 'interfaces/Know'
import Checkbox from 'components/elements/Inputs/Checkbox'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import Row from 'components/elements/Row'
import { useRouter } from 'next/router'
import { parseLocale } from 'lib/constants/localization'
import React from 'react'

const loadingAreas = {
    create: 'createReport',
}

const ReportIcon = styled(MenuIcon)`
    cursor: pointer;
    scale: 1.25;
`

const ReportOption = styled.div<{ isSelected?: boolean }>`
    width: 100%;
    padding: 15px;
    border-radius: ${theme.borderRadius.normal};
    cursor: pointer;
    border: 2px solid ${({ isSelected }) => (isSelected ? theme.colors.deepSeaBlue : theme.colors.violet)};
`

interface ReportFormProps {
    description: string
    legalConfirmation: boolean
}

interface Props {
    reportType: ReportType
    reportedUserUuid: string
    chatMessage?: DisplayChatMessage | null
    know?: Know | null
    icon?: JSX.Element
}

const ReportDialog = ({ chatMessage, reportType, reportedUserUuid, know, icon }: Props) => {
    const [reportReasons, setReportReasons] = useState<ReportReason[]>()
    const [openReportDialog, toggleReportDialog] = useToggleState(false)
    const [selectedReason, setSelectedReason] = useState<ReportReason>()
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const reportFormRef = useRef<FormHandles>(null)
    const { locale } = useRouter()
    const parsedLocale = parseLocale(locale)
    const { t: tCommon } = _useTranslation('common')

    const showAnonymousReportCheckbox =
        !authenticatedUser || (authenticatedUser && !!selectedReason?.canBeReportedAnonymously)

    useEffect(() => {
        UserReportService.getReasons(reportType, parsedLocale.interfaceLanguageCode)
            .then((reasons) => setReportReasons(reasons))
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'userReport.getReasons')
                } else {
                    throw error
                }
            })
    }, [reportType, tCommon, parsedLocale])

    const handleUserReportSubmit: SubmitHandler<ReportFormProps> = ({ description, legalConfirmation }) => {
        reportFormRef.current!.setErrors({})

        if (!selectedReason) {
            toast.warning(tCommon('common/reportCreateMissingReasonErrorMessage'))
            return
        }

        if (!description) {
            reportFormRef?.current?.setFieldError('description', tCommon('common/formValidationRequiredError'))
            return
        }

        if (showAnonymousReportCheckbox && !legalConfirmation) {
            reportFormRef?.current?.setFieldError('legalConfirmation', tCommon('common/reportLegalConfirmationError'))
            return
        }

        let reportRequestBody: UserReportRequest = {
            description: description,
            reportType: reportType,
            reportedChatMessageUuid: chatMessage ? chatMessage?.uuid : null,
            isAnonymous: showAnonymousReportCheckbox,
            userReason: selectedReason.name,
        }

        if (reportedUserUuid) {
            reportRequestBody = {
                ...reportRequestBody,
                reportedUserUuid: reportedUserUuid,
            }
        }

        if (know) {
            reportRequestBody = {
                ...reportRequestBody,
                reportedKnowUuid: know.uuid,
            }
        }

        trackPromise(
            UserReportService.create(reportRequestBody)
                .then(() => {
                    toggleReportDialog()
                    toast.success('Report was submitted')
                })
                .catch((error) => {
                    if (error instanceof ApiError) {
                        error.handleUnknownError(tCommon, 'userReport.create')
                    } else {
                        throw error
                    }
                }),
            loadingAreas.create
        )
    }

    return (
        <>
            {icon ? (
                <div onClick={toggleReportDialog}>{icon}</div>
            ) : (
                <Row gap={10}>
                    <br />
                    <ReportIcon onClick={toggleReportDialog} />
                    {/* <Body2 fontFamily="var(--font-inter)" fontWeigth={400}>
                    {tCommon('common/reportButtonLabel')}
                </Body2> */}
                </Row>
            )}
            <FeatureDialog
                open={openReportDialog}
                onClose={toggleReportDialog}
                ariaPrefix="create-report"
                title={tCommon('common/reportCreateTitle')}
                maxWidth={500}
                allowVerticalOverflow
                disableBackdropClick
            >
                <br />
                <Stack>
                    <Form onSubmit={handleUserReportSubmit} ref={reportFormRef}>
                        <Stack gutter={5}>
                            {reportReasons?.length
                                ? reportReasons.map((reason) => (
                                      <ReportOption
                                          isSelected={selectedReason?.name === reason.name}
                                          key={reason.name}
                                          onClick={() => setSelectedReason(reason)}
                                      >
                                          <Body1 fontFamily="var(--font-inter)">{tCommon(reason.name)}</Body1>
                                      </ReportOption>
                                  ))
                                : null}
                        </Stack>
                        <br />
                        <Textarea
                            label=""
                            placeholder={tCommon('common/reportCreateReasonHint')}
                            name="description"
                            height={120}
                        />
                        {showAnonymousReportCheckbox ? (
                            <Checkbox
                                label={
                                    <Body1 fontFamily="var(--font-inter)" fontWeight={400}>
                                        {tCommon('common/reportLegalConfirmationLabel')}
                                    </Body1>
                                }
                                name="legalConfirmation"
                                placeholder=""
                                noMargin
                            />
                        ) : null}
                        <br />
                        <NewButton
                            fullWidth
                            loadingArea={loadingAreas.create}
                            color={theme.colors.deepSeaBlue}
                            borderRadius={theme.borderRadius.normal}
                        >
                            <ButtonText color={theme.colors.white}>{tCommon('common/submit')}</ButtonText>
                        </NewButton>
                    </Form>
                </Stack>
            </FeatureDialog>
        </>
    )
}

export default ReportDialog
